'use client';

import * as React from 'react';
import dynamic from 'next/dynamic';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { X } from 'lucide-react';
import { getThemeById, DEFAULT_THEME_ID } from '@/constants/themes';
import { resolvePresetForMode } from '@/lib/themePreset';
import { AppThemeContext } from '@/lib/themeTokens';
import { marketingVars } from '@/lib/marketingTheme';
import { ShadcnThemeSync } from '@/lib/shadcnTheme';
import AuthChecker from '@/components/auth/AuthChecker';
// Signed-in-only widgets, loaded on demand.
//
// These were imported eagerly and mounted for every visitor, so a builder
// reading /pricing downloaded the ⌘K palette (cmdk + dialog + entity search),
// the web-push subscriber and the AI assistant before the page was usable.
// None of them can do anything without a session: the palette searches a
// tenant's entities, push subscribes a signed-in device, the assistant is
// gated on a tenant add-on. They are now split out of the main bundle and
// mounted only once there is a token.
const CommandPalette = dynamic(() => import('@/components/CommandPalette'), { ssr: false });
import ConfirmRoot from '@/components/ConfirmRoot';
import ServiceWorkerRegister from '@/components/ServiceWorkerRegister';
const PushNotifications = dynamic(() => import('@/components/PushNotifications'), { ssr: false });
const AssistantWidget = dynamic(() => import('@/components/ai/AssistantWidget'), { ssr: false });
import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { store, type RootState } from '@/store';

// Stable no-op subscribe for useSyncExternalStore — the hydration flag never
// changes after mount, so there's nothing to subscribe to.
const emptySubscribe = () => () => {};

/**
 * Publishes the active theme (preset/custom + light/dark mode) to the app.
 * Sits inside ReduxProvider so it can read `ui.*` and rebuild when the toggle
 * flips. The app is 100% shadcn/Tailwind now — theming flows through CSS
 * variables: `ShadcnThemeSync` writes the shadcn `--*` tokens, and
 * `AppThemeContext` feeds `useThemeTokens()` for the few components that read
 * raw token values. No MUI theme object is built any more.
 */
function ThemedShell({ children }: { children: React.ReactNode }) {
  const themeId = useSelector((s: RootState) => s.ui.themeId);
  const themeMode = useSelector((s: RootState) => s.ui.themeMode);
  const customTheme = useSelector((s: RootState) => s.ui.customTheme);
  const marketingTheme = useSelector((s: RootState) => s.ui.marketingTheme);
  // Gates the signed-in-only widgets below. Read off the same auth slice
  // AuthChecker uses, so a marketing visitor never downloads them and a
  // logged-in user gets them as soon as the token is hydrated.
  const signedIn = useSelector((s: RootState) => !!s.auth.token);

  // SSR has no localStorage, so the server renders the committed DEFAULT theme.
  // The persisted themeId only exists on the client; applying it on the first
  // client render would produce different class names / CSS vars than the server
  // sent → hydration mismatch. Gate behind a hydration flag (useSyncExternalStore
  // returns the server snapshot `false` during SSR + hydration, then `true`):
  // first client render matches the server (default), then we swap to the
  // persisted theme — which, for the committed product, is the same default, so
  // there is no flash.
  const hydrated = React.useSyncExternalStore(emptySubscribe, () => true, () => false);

  // The global light/dark toggle is authoritative. Like the preset, the
  // persisted mode only exists client-side, so render the default (light)
  // until hydrated to keep SSR and the first client render in agreement.
  const mode = hydrated ? themeMode : 'light';
  // A super-admin custom theme (Theme Customizer) wins over the preset app-wide;
  // it carries both palettes, so pick by the current mode. Gated on `hydrated`
  // like themeId so SSR + first client render stay on the default (no mismatch).
  const basePreset = React.useMemo(
    () => (hydrated && customTheme
      ? (mode === 'dark' ? customTheme.dark : customTheme.light)
      : getThemeById(hydrated ? themeId : DEFAULT_THEME_ID)),
    [hydrated, customTheme, mode, themeId],
  );
  // Resolve the preset against the requested mode so the global toggle wins:
  // a light preset toggled to dark yields a coherent dark, same-brand palette.
  const preset = React.useMemo(() => resolvePresetForMode(basePreset, mode), [basePreset, mode]);
  const isDark = mode === 'dark';

  // Keep the shadcn/ui token layer in sync with the app's light/dark toggle.
  // shadcn reads its CSS variables from a `.dark` class on <html>; the source of
  // truth is the Redux `ui.themeMode` flag. Mirror it here so Tailwind pages
  // follow the same toggle.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  // Marketing surfaces (login, landing) read `--mk-*` CSS vars. The root layout
  // publishes them server-side, but override on the client from the platform
  // MARKETING theme (its light palette) so logged-out login/marketing follow it
  // even before/if the server fetch is stale. This is intentionally the
  // marketing theme, NOT the per-tenant app theme (customTheme).
  React.useEffect(() => {
    if (typeof document === 'undefined' || !hydrated || !marketingTheme) return;
    const vars = marketingVars(marketingTheme.light) as Record<string, string>;
    const body = document.body;
    for (const k in vars) body.style.setProperty(k, vars[k]);
  }, [hydrated, marketingTheme]);

  return (
    <AppThemeContext.Provider value={{ preset, mode }}>
      {/* Publishes the active (preset/custom + mode) theme as shadcn CSS
          variables so every shadcn page follows the same theme. */}
      <ShadcnThemeSync />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: isDark ? '#0f172a' : '#fff',
            color: isDark ? '#f8fafc' : '#0f172a',
            fontSize: '14px',
            padding: '12px 16px',
            borderRadius: '6px',
            boxShadow: isDark ? '0 4px 16px rgba(0, 0, 0, 0.5)' : '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
          success: { iconTheme: { primary: '#10b981', secondary: isDark ? '#0f172a' : '#fff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: isDark ? '#0f172a' : '#fff' } },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {icon}
                {message}
                {t.type !== 'loading' && (
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => toast.dismiss(t.id)}
                    style={{
                      marginLeft: 4,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 4,
                      borderRadius: 6,
                      color: '#94a3b8',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>
      <AuthChecker>{children}</AuthChecker>
      {/* Global confirm dialog — drives the app-wide "confirm before update" gate. */}
      <ConfirmRoot />
      {/* Command palette — opens on ⌘K. Signed-in only: it searches tenant
          entities, so there is nothing for it to do before login. */}
      {signedIn && <CommandPalette />}
      {/* PWA: registers the service worker so the app is installable + offline-resilient. */}
      <ServiceWorkerRegister />
      {/* Web Push: subscribes signed-in devices + plays foreground custom sound. */}
      {signedIn && <PushNotifications />}
      {/* AI assistant: a floating help button on authenticated tenant pages.
          Renders null unless the tenant owns the `ai` add-on AND every
          availability rung passes, so it costs nothing for everyone else. */}
      {signedIn && <AssistantWidget />}
    </AppThemeContext.Provider>
  );
}

export default function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ReduxProvider store={store}>
      <ThemedShell>{children}</ThemedShell>
    </ReduxProvider>
  );
}
